/* Small prose helpers shared by the docs pages, so page files stay mostly
 * content. Styling-only — all data flows through useSnippetContext(). */

import { type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useWsPath } from "../../workspaces";

/** A titled section of a docs page. */
export function DocSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-h5 font-semibold text-fg-1">{title}</h2>
      {children}
    </section>
  );
}

/** Body copy with the docs' standard measure and tone. */
export function DocText({ children }: { children: ReactNode }) {
  return <p className="max-w-2xl text-body-sm text-fg-2">{children}</p>;
}

/** A numbered quickstart step. Render inside an <ol className="flex flex-col gap-6">. */
export function Step({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: ReactNode;
}) {
  return (
    <li className="flex gap-3.5">
      <span
        aria-hidden
        className="flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-body-sm font-semibold text-brand-800"
      >
        {number}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <h2 className="text-h6 font-semibold text-fg-1">{title}</h2>
        {children}
      </div>
    </li>
  );
}

/** Inline code in prose. */
export function InlineCode({ children }: { children: ReactNode }) {
  return (
    <code className="rounded bg-surface-muted px-1 py-0.5 font-mono text-[0.92em] text-fg-1">
      {children}
    </code>
  );
}

/** Workspace-aware in-app text link (pass a workspace-relative path). */
export function DocLink({ to, children }: { to: string; children: ReactNode }) {
  const ws = useWsPath();
  return (
    <Link
      to={ws(to)}
      className="font-medium text-fg-link no-underline hover:text-brand-700"
    >
      {children}
    </Link>
  );
}

/** Text link out of the app (the customer docs site, a provider's docs) —
 *  same treatment as DocLink, opened in a new tab. */
export function DocExternalLink({
  href,
  children,
}: {
  href: string;
  children: ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="font-medium text-fg-link no-underline hover:text-brand-700"
    >
      {children}
    </a>
  );
}

/** Inline link-styled button for in-prose actions (e.g. open a modal) — reads
 *  like a DocLink but triggers an action instead of navigating. */
export function DocLinkButton({
  onClick,
  children,
}: {
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="font-medium text-fg-link no-underline hover:text-brand-700"
    >
      {children}
    </button>
  );
}

/** One-line caveat under a snippet (e.g. the placeholder-URL note). */
export function DocNote({ children }: { children: ReactNode }) {
  return <p className="max-w-2xl text-body-sm text-fg-3">{children}</p>;
}
